import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SelectOption } from 'tea-component';

import type { TAiAnalysisSettingsModalType } from './index';
import type { IAiAnalysisScope, IAiPromptInfo, IAiPromptOption } from '../types';
import {
  buildEditorDraftFromParams,
  CUSTOM_APPROACH_VALUE,
  patchEditorDraft,
  type IEditorDraft,
} from './editorDraft';
import { normalizeAnalysisScope } from './scope';

export interface IUseSettingsStateResult {
  options: IAiPromptOption[];
  selectedApproachId: number | string;
  promptContent: string;
  analysisContext: string;
  analysisScope: IAiAnalysisScope;
  scopeErrorKey: string;
  confirmLoading: boolean;
  isConfirmLocked: boolean;
  handlePreview: () => void;
  handleAnalysisContextChange: (value: string) => void;
  setAnalysisScope: (value: IAiAnalysisScope) => void;
  handleApproachSelectChange: (
    value: number | string,
    context: { event: unknown; option?: SelectOption; },
  ) => void;
  handlePromptContentChange: (value: string) => void;
  validateScopeBeforeSubmit: () => boolean;
  buildPromptInfoForConfirm: () => Promise<IAiPromptInfo>;
  handleConfirm: () => Promise<void>;
  resetToInitialState: () => void;
  /** 保存模板后选中对应预置思路（非自定义），正文与弹窗提交一致 */
  applyPresetApproachSelection: (
    approachId: number | string,
    content: { promptContent: string; analysisContext: string; },
  ) => void;
}

export interface IUseSettingsStateParams {
  promptOptions: IAiPromptOption[];
  allTaskIds: string[];
  defaultSelectedApproachId?: number | string;
  initialPromptInfo?: IAiPromptInfo;
  onPreview?: () => void;
  onChangeType: (type: TAiAnalysisSettingsModalType) => void;
  onEditConfirm: (promptInfo: IAiPromptInfo) => void;
}

/**
 * 预览态改回预置原文时，用于恢复此前选中的分析思路。
 */
interface IAutoRestoreApproachContext {
  selectedApproachId: number | string;
  promptContent: string;
  analysisContext: string;
}

/**
 * 用户自定义 prompt 的草稿数据，用于切换时保留用户输入。
 */
interface ICustomPromptDraft {
  id?: number;
  promptContent?: string;
  analysisContext?: string;
}

/**
 * 统一管理 Settings 组件的请求与输入状态，避免视图层耦合数据来源。
 */
export function useSettingsState(params: IUseSettingsStateParams): IUseSettingsStateResult {
  const {
    promptOptions,
    allTaskIds,
    defaultSelectedApproachId,
    initialPromptInfo,
    onPreview,
    onChangeType,
    onEditConfirm,
  } = params;

  // 编辑器草稿状态：包含当前选中的方案、prompt 内容、分析上下文、分析范围等
  const [editorDraft, setEditorDraft] = useState<IEditorDraft>(() => buildEditorDraftFromParams({
    promptOptions,
    allTaskIds,
    defaultSelectedApproachId,
    initialPromptInfo,
  }));
  // 分析范围校验错误对应的国际化 key
  const [scopeErrorKey, setScopeErrorKey] = useState<string>('');
  // 预览态：预置思路下改正文/背景会切自定义，此处记录切走前的预置 id 与原文以便改回时恢复
  const autoRestoreApproachRef = useRef<IAutoRestoreApproachContext | null>(null);
  // 保留最近一次自定义 prompt 的内容，支持从预设方案切回自定义时恢复用户输入
  const lastCustomPromptRef = useRef<ICustomPromptDraft | undefined>(initialPromptInfo?.customPrompt);

  const options = useMemo<IAiPromptOption[]>(() => promptOptions, [promptOptions]);
  const { selectedApproachId, promptContent, analysisContext, analysisScope } = editorDraft;

  // 局部更新编辑器草稿，自动合并到现有状态
  const updateEditorDraft = useCallback((patch: Partial<IEditorDraft>) => {
    setEditorDraft((prevDraft) => patchEditorDraft(prevDraft, patch));
  }, []);

  // 重置为初始状态，仅在弹窗重新打开等显式场景由上层调用
  const resetToInitialState = useCallback(() => {
    const nextDraft = buildEditorDraftFromParams({
      promptOptions,
      allTaskIds,
      defaultSelectedApproachId,
      initialPromptInfo,
    });
    // 若初始状态为自定义且存在 customPrompt，则同步到 lastCustomPromptRef，便于后续切回恢复
    if (nextDraft.selectedApproachId === CUSTOM_APPROACH_VALUE && initialPromptInfo?.customPrompt) {
      lastCustomPromptRef.current = initialPromptInfo.customPrompt;
    }
    setEditorDraft(nextDraft);
    autoRestoreApproachRef.current = null;
    setScopeErrorKey('');
  }, [
    allTaskIds,
    defaultSelectedApproachId,
    initialPromptInfo,
    promptOptions,
  ]);

  // 当初始 prompt 的自定义内容变化时，同步更新 lastCustomPromptRef
  useEffect(() => {
    if (!initialPromptInfo?.customPrompt) {
      return;
    }
    lastCustomPromptRef.current = initialPromptInfo.customPrompt;
  }, [initialPromptInfo?.customPrompt?.analysisContext, initialPromptInfo?.customPrompt?.promptContent]);

  // 在自定义模式下，实时保存用户的 prompt 内容和分析上下文到 lastCustomPromptRef
  useEffect(() => {
    if (selectedApproachId !== CUSTOM_APPROACH_VALUE) {
      return;
    }
    lastCustomPromptRef.current = {
      ...lastCustomPromptRef.current,
      promptContent,
      analysisContext,
    };
  }, [analysisContext, promptContent, selectedApproachId]);

  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview();
      return;
    }
    onChangeType('report');
  }, [onChangeType, onPreview]);

  // 处理方案（预设 / 自定义）切换
  const handleApproachSelectChange = useCallback((
    value: number | string,
    context: { event: unknown; option?: SelectOption; },
  ) => {
    // 切换到自定义模式：优先恢复上次自定义输入，否则仅切换 selectedApproachId
    if (value === CUSTOM_APPROACH_VALUE) {
      autoRestoreApproachRef.current = null;
      if (lastCustomPromptRef.current) {
        updateEditorDraft({
          selectedApproachId: value,
          promptContent: String(lastCustomPromptRef.current.promptContent ?? ''),
          analysisContext: String(lastCustomPromptRef.current.analysisContext ?? ''),
        });
        return;
      }
      updateEditorDraft({ selectedApproachId: value });
      return;
    }

    // 切换到预设方案：从 option 中读取对应的 prompt 和分析上下文
    const selectedOption = context?.option as IAiPromptOption | undefined;
    const {
      analysisContext = '',
      promptContent = '',
    } = selectedOption?.info ?? {};
    updateEditorDraft({
      selectedApproachId: value,
      promptContent: String(promptContent),
      analysisContext: String(analysisContext),
    });
    autoRestoreApproachRef.current = null;
  }, [updateEditorDraft]);

  /** 预览态：正文与背景均与预置原文一致时，恢复此前选中的预置分析思路 */
  const tryRestorePresetApproachIfDraftMatches = useCallback((
    nextPromptContent: string,
    nextAnalysisContext: string,
  ): void => {
    const restore = autoRestoreApproachRef.current;
    if (!restore) {
      return;
    }
    const previousApproachIdStr = String(restore.selectedApproachId);
    const optionStillExists = options.some(
      (opt) => String(opt.value) === previousApproachIdStr,
    );
    if (
      !optionStillExists
      || nextPromptContent !== restore.promptContent
      || nextAnalysisContext !== restore.analysisContext
    ) {
      return;
    }
    updateEditorDraft({
      selectedApproachId: restore.selectedApproachId,
      promptContent: restore.promptContent,
      analysisContext: restore.analysisContext,
    });
    autoRestoreApproachRef.current = null;
  }, [options, updateEditorDraft]);

  const capturePresetApproachForAutoRestore = useCallback((): IAutoRestoreApproachContext => {
    const selectedOption = options.find(
      (opt) => String(opt?.value) === String(selectedApproachId),
    );
    const {
      promptContent: presetPromptContent = '',
      analysisContext: presetAnalysisContext = '',
    } = selectedOption?.info ?? {};
    return {
      selectedApproachId,
      promptContent: String(presetPromptContent),
      analysisContext: String(presetAnalysisContext),
    };
  }, [options, selectedApproachId]);

  // 改预置正文会自动切「自定义洞察思路」；改回原文可恢复此前选中的预置项（编辑/预览一致）
  const handlePromptContentChange = useCallback((value: string) => {
    if (selectedApproachId === CUSTOM_APPROACH_VALUE) {
      updateEditorDraft({ promptContent: value });
      tryRestorePresetApproachIfDraftMatches(value, analysisContext);
      return;
    }

    const presetSnapshot = capturePresetApproachForAutoRestore();
    if (value !== presetSnapshot.promptContent) {
      autoRestoreApproachRef.current = presetSnapshot;
      updateEditorDraft({
        selectedApproachId: CUSTOM_APPROACH_VALUE,
        promptContent: value,
      });
      return;
    }
    updateEditorDraft({ promptContent: value });
  }, [
    analysisContext,
    capturePresetApproachForAutoRestore,
    selectedApproachId,
    tryRestorePresetApproachIfDraftMatches,
    updateEditorDraft,
  ]);

  // 改预置分析背景会自动切「自定义洞察思路」；改回原文可恢复此前选中的预置项（编辑/预览一致）
  const handleAnalysisContextChange = useCallback((value: string) => {
    if (selectedApproachId === CUSTOM_APPROACH_VALUE) {
      updateEditorDraft({ analysisContext: value });
      tryRestorePresetApproachIfDraftMatches(promptContent, value);
      return;
    }

    const presetSnapshot = capturePresetApproachForAutoRestore();
    if (value !== presetSnapshot.analysisContext) {
      autoRestoreApproachRef.current = presetSnapshot;
      updateEditorDraft({
        selectedApproachId: CUSTOM_APPROACH_VALUE,
        analysisContext: value,
      });
      return;
    }
    updateEditorDraft({ analysisContext: value });
  }, [
    capturePresetApproachForAutoRestore,
    promptContent,
    selectedApproachId,
    tryRestorePresetApproachIfDraftMatches,
    updateEditorDraft,
  ]);

  // 更新分析范围，同时清空之前的校验错误
  const setAnalysisScope = useCallback((value: IAiAnalysisScope) => {
    setScopeErrorKey('');
    updateEditorDraft({
      analysisScope: normalizeAnalysisScope(value, allTaskIds),
    });
  }, [allTaskIds, updateEditorDraft]);

  // 提交前校验分析范围：图表类型必须至少选择一项
  const validateScopeBeforeSubmit = useCallback(() => {
    if (analysisScope.type === 'chart' && analysisScope.taskIds.length === 0) {
      setScopeErrorKey('dbl_ai_analysis_settings_scope_select_at_least_one_chart');
      return false;
    }
    setScopeErrorKey('');
    return true;
  }, [analysisScope.taskIds.length, analysisScope.type]);

  // 根据当前编辑器状态构建用于确认的 IAiPromptInfo 对象
  const buildPromptInfoForConfirm = useCallback(async (): Promise<IAiPromptInfo> => {
    // 预设方案：附带历史 customPrompt，支持后续切回自定义时恢复输入
    if (selectedApproachId !== CUSTOM_APPROACH_VALUE) {
      const numericPromptId = Number(selectedApproachId);
      const promptInfo: IAiPromptInfo = {
        analysisScope: normalizeAnalysisScope(analysisScope, allTaskIds),
        promptId: numericPromptId,
        isCustom: false,
      };
      if (lastCustomPromptRef.current) {
        promptInfo.customPrompt = lastCustomPromptRef.current;
      }
      return promptInfo;
    }

    // 自定义方案：同步最新内容到 lastCustomPromptRef 并返回自定义数据
    lastCustomPromptRef.current = {
      promptContent,
      analysisContext,
    };

    return {
      analysisScope: normalizeAnalysisScope(analysisScope, allTaskIds),
      isCustom: true,
      customPrompt: {
        promptContent,
        analysisContext,
      },
    };
  }, [
    analysisContext,
    allTaskIds,
    analysisScope,
    promptContent,
    selectedApproachId,
  ]);

  // 保存模板成功后：切到刚保存的预置项，避免仍停留在「自定义洞察思路」
  const applyPresetApproachSelection = useCallback((
    approachId: number | string,
    content: { promptContent: string; analysisContext: string; },
  ): void => {
    updateEditorDraft({
      selectedApproachId: approachId,
      promptContent: String(content.promptContent ?? ''),
      analysisContext: String(content.analysisContext ?? ''),
    });
    autoRestoreApproachRef.current = null;
  }, [updateEditorDraft]);

  // 确认提交：先校验范围，再构建 promptInfo 并回调给上层
  const handleConfirm = useCallback(async () => {
    if (!validateScopeBeforeSubmit()) {
      return;
    }
    const promptInfo = await buildPromptInfoForConfirm();
    onEditConfirm(promptInfo);
  }, [
    buildPromptInfoForConfirm,
    onEditConfirm,
    validateScopeBeforeSubmit,
  ]);

  return {
    options,
    selectedApproachId,
    promptContent,
    analysisContext,
    analysisScope,
    scopeErrorKey,
    confirmLoading: false,
    isConfirmLocked: false,
    handlePreview,
    handleAnalysisContextChange,
    setAnalysisScope,
    handleApproachSelectChange,
    handlePromptContentChange,
    validateScopeBeforeSubmit,
    buildPromptInfoForConfirm,
    handleConfirm,
    resetToInitialState,
    applyPresetApproachSelection,
  };
}

/**
 * Reports 视图返回 Settings 所需的状态和回调。
 */
export interface IUseReportsStateResult {
  handleBackToSettings: () => void;
}

/**
 * 管理 Reports 视图中返回 Settings 的交互逻辑。
 */
export function useReportsState(
  onChangeType: (type: TAiAnalysisSettingsModalType) => void,
  onBackToSettings?: () => void,
): IUseReportsStateResult {
  const handleBackToSettings = useCallback(() => {
    onBackToSettings?.();
    onChangeType('setting');
  }, [onBackToSettings, onChangeType]);

  return {
    handleBackToSettings,
  };
}
